"use client";

import { useMemo, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { startCheckoutCall } from "@/lib/backend";

/* Frequently Bought Together — Shopify-style stacked card.
 *
 * Brian QA: "in product details page after the buy with stripe section
 * it should show like the image i have given u, and there is no add
 * to cart option and the card should be working" + "buy this template
 * canva any randomly any notion or match the perfect bundles". This
 * is the Frequently Bought Together pattern with checkboxes per row,
 * a computed total, and a working add-to-cart that opens Stripe with
 * all checked items — and the items can be CROSS-BRAND (a wall-art
 * print + a Notion template + a Canva pack in one bundle).
 *
 * Items are checkable individually. Total updates live. The button
 * passes every selected id+kind to the right backend path
 * (/api/shop/checkout for products, /api/v1/checkout/start for
 * notion-template and template_pack), so the buyer lands on a Stripe
 * checkout pre-loaded with everything they ticked. */

export type FBTKind = "product" | "notion-template" | "template_pack";

export type FBTItem = {
  id: string;
  slug: string;
  title: string;
  cover: string;
  priceCents: number;
  /* Which checkout endpoint to use. Default = "product"
   * (shop_products row → /api/shop/checkout). */
  kind?: FBTKind;
  /* Optional kicker (e.g. "Same family · 1×1 square") shown above
   * the title. */
  kicker?: string;
};

/* Wall Art SOP Gate F / Gate G — the heading used to be hardcoded "Complete
 * the wall." on EVERY product type (it showed on Canva, Notion, sticker, and
 * 3D pages too). Derive a product-appropriate heading from the product's own
 * section path (+ kind) so the copy stops bleeding across departments. Callers
 * can still override with `heading`. */
function headingFor(cancelPath: string, kind?: FBTKind): string {
  const p = (cancelPath || "").toLowerCase();
  if (p.includes("/digital-art") || p.startsWith("/art") || p.includes("wall-art")) return "Complete the wall.";
  if (p.includes("/sticker")) return "Complete the set.";
  if (p.includes("/3d")) return "Complete the collection.";
  if (kind === "template_pack" || p.includes("/template") || p.includes("/canva")) return "Complete the toolkit.";
  if (kind === "notion-template" || p.includes("/notion")) return "Complete your stack.";
  return "Build your bundle.";
}

export function FrequentlyBoughtTogether({
  main,
  bundle,
  cancelPath,
  heading,
}: {
  main: FBTItem;
  bundle: FBTItem[];
  cancelPath: string;
  heading?: string;
}) {
  const sectionHeading = heading ?? headingFor(cancelPath, main.kind);
  const all = useMemo(() => [main, ...bundle], [main, bundle]);
  const [checked, setChecked] = useState<Record<string, boolean>>(() => {
    /* Default state: main item ticked, every suggestion ticked too —
     * matches Shopify FBT default and maximizes AOV. The buyer
     * untoggles what they don't want. */
    const init: Record<string, boolean> = {};
    for (const it of all) init[it.id] = true;
    return init;
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selected = all.filter((it) => checked[it.id]);
  const subtotalCents = selected.reduce((s, it) => s + it.priceCents, 0);
  /* SOP Phase 1.5 — the previous "anchor total" used a deterministic
   * 30-60% per-slug hash to fabricate a "$You save X" framing. Killed
   * per the no-perpetual-fake-sale rule. If/when the backend exposes a
   * real bundle discount, reintroduce a "you save" display tied to
   * that real number — never to a hash. */
  const anchorTotalCents = subtotalCents;
  const youSaveCents = 0;

  const onCheckout = async () => {
    if (busy || selected.length === 0) return;
    setBusy(true);
    setError(null);
    try {
      const { url } = await startCheckoutCall({
        items: selected.map((it) => ({
          /* Per-item kind so the bundle can mix wall-art products,
           * shop bundles, and Notion templates in one Stripe session. */
          kind: (it.kind ?? "product") as
            | "product"
            | "bundle"
            | "notion-template",
          id: it.id,
          quantity: 1,
        })),
        successPath: `/thanks?slug=${main.slug}&session={CHECKOUT_SESSION_ID}`,
        cancelPath,
      });
      window.location.href = url;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Checkout unavailable.");
      setBusy(false);
    }
  };

  if (bundle.length === 0) return null;

  return (
    <section
      className="rounded-2xl border p-5 md:p-7"
      style={{
        borderColor: "rgba(217,163,90,0.4)",
        background:
          "linear-gradient(180deg, rgba(217,163,90,0.06) 0%, rgba(0,0,0,0.4) 100%)",
      }}
    >
      <div className="mb-5 flex flex-wrap items-baseline justify-between gap-3">
        <div>
          <div
            className="font-mono text-[10px] uppercase mb-2 inline-flex items-center gap-2"
            style={{
              color: "var(--color-accent)",
              letterSpacing: "0.16em",
            }}
          >
            <span className="h-1.5 w-1.5 rounded-full bg-current animate-pulse" />
            Frequently bought together
          </div>
          <h3
            className="text-white"
            style={{
              fontFamily: "var(--font-display)",
              fontSize: "clamp(22px, 2.6vw, 30px)",
              lineHeight: 1.05,
              letterSpacing: "-0.02em",
            }}
          >
            {sectionHeading}
          </h3>
        </div>
        {youSaveCents > 0 && (
          <span
            className="rounded-full px-3 py-1 font-mono text-[11px] uppercase text-black"
            style={{
              background: "var(--color-accent)",
              letterSpacing: "0.12em",
              fontWeight: 600,
            }}
          >
            Save ${(youSaveCents / 100).toFixed(0)}
          </span>
        )}
      </div>

      <ul className="space-y-3">
        {all.map((it, i) => {
          const isMain = i === 0;
          const isChecked = !!checked[it.id];
          return (
            <li key={it.id}>
              <label
                className="flex cursor-pointer items-center gap-3 rounded-xl border px-3 py-3 transition-colors"
                style={{
                  borderColor: isChecked
                    ? "rgba(217,163,90,0.5)"
                    : "rgba(255,255,255,0.08)",
                  background: isChecked
                    ? "rgba(217,163,90,0.07)"
                    : "rgba(255,255,255,0.02)",
                }}
              >
                <input
                  type="checkbox"
                  checked={isChecked}
                  onChange={(e) =>
                    setChecked((prev) => ({
                      ...prev,
                      [it.id]: e.target.checked,
                    }))
                  }
                  disabled={isMain && selected.length === 1 && isChecked}
                  className="h-5 w-5 shrink-0 cursor-pointer accent-[color:var(--color-accent)]"
                  data-interactive
                />
                <div
                  className="relative h-16 w-16 shrink-0 overflow-hidden rounded-md border"
                  style={{
                    borderColor: "rgba(255,255,255,0.08)",
                    background: "rgba(255,255,255,0.02)",
                  }}
                >
                  {it.cover && (
                    <Image
                      src={it.cover}
                      alt={it.title}
                      fill
                      sizes="64px"
                      className="object-cover"
                    />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div
                    className="font-mono text-[10px] uppercase"
                    style={{
                      color: "var(--color-accent)",
                      letterSpacing: "0.14em",
                    }}
                  >
                    {it.kicker ?? (isMain ? "This print" : "Add this")}
                  </div>
                  <Link
                    href={`/digital-art/${it.slug}`}
                    onClick={(e) => e.stopPropagation()}
                    className="mt-0.5 block truncate text-[14px] text-white hover:underline"
                  >
                    {it.title}
                  </Link>
                </div>
                <Price priceCents={it.priceCents} slug={it.slug} />
              </label>
            </li>
          );
        })}
      </ul>

      <div
        className="mt-5 flex flex-wrap items-center justify-between gap-3 border-t pt-5"
        style={{ borderColor: "rgba(255,255,255,0.06)" }}
      >
        <div>
          <div
            className="font-mono text-[10px] uppercase"
            style={{
              color: "rgba(255,255,255,0.6)",
              letterSpacing: "0.14em",
            }}
          >
            Total · {selected.length} item{selected.length === 1 ? "" : "s"}
          </div>
          <div className="mt-1 flex items-baseline gap-2">
            <span
              className="font-mono text-[28px] text-white"
              style={{
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.02em",
                fontWeight: 600,
              }}
            >
              ${(subtotalCents / 100).toFixed(0)}
            </span>
            {anchorTotalCents > subtotalCents && (
              <span
                className="font-mono text-[16px]"
                style={{
                  color: "rgba(255,255,255,0.4)",
                  textDecoration: "line-through",
                }}
              >
                ${(anchorTotalCents / 100).toFixed(0)}
              </span>
            )}
          </div>
        </div>
        <button
          type="button"
          onClick={onCheckout}
          disabled={busy || selected.length === 0}
          data-interactive
          className="rounded-lg px-5 py-3 text-[14px] font-medium disabled:opacity-50"
          style={{
            background: "var(--color-accent)",
            color: "#000",
            minWidth: 220,
          }}
        >
          {busy
            ? "Opening checkout…"
            : `Add ${selected.length} to cart →`}
        </button>
      </div>
      {error && (
        <p className="mt-3 text-[13px]" style={{ color: "#ef4444" }}>
          {error}
        </p>
      )}
      <p
        className="mt-3 font-mono text-[10px] uppercase"
        style={{
          color: "rgba(255,255,255,0.45)",
          letterSpacing: "0.14em",
        }}
      >
        ↳ Stripe checkout · 14-day guarantee · Instant download
      </p>
    </section>
  );
}

function Price({ priceCents }: { priceCents: number; slug?: string }) {
  /* SOP Phase 1.5 — strikethrough anchor removed. Single-product price
   * only; the bundle "you save" framing now waits for a real backend
   * discount instead of a hash-fabricated one. */
  return (
    <div
      className="shrink-0 text-right"
      style={{ fontVariantNumeric: "tabular-nums" }}
    >
      <div
        className="font-mono text-[15px] text-white"
        style={{ fontWeight: 600 }}
      >
        ${(priceCents / 100).toFixed(0)}
      </div>
    </div>
  );
}
